import {
  BasePaginateParamsSchema,
  BasePaginateResultSchema,
} from '@/api/common.ts'
import dayjs from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { z } from 'zod'
dayjs.extend(weekOfYear)

export const Prd4010SearchParamsSchema = z
  .object({
    number: z.string().optional(),
    partyId: z.string().optional(),
    itemId: z.string().optional(),
    lotNo: z.string().optional(),
    status: z
      .union([z.literal('1'), z.literal('2'), z.literal('3'), z.literal('4')])
      .optional(),
    filterStatus: z.string().optional(),
    /**
     * The .preprocess step ensures that if the value is '' or null, it becomes undefined before Zod tries to coerce it to a date.
     * This prevents the default date (1970-01-01) from being used in your API query when the field is cleared.
     */
    mfgDate: z
      .preprocess(
        (val) => (val === '' || val === null ? undefined : val),
        z.coerce.date().optional()
      )
      .optional(),
    startDate: z
      .preprocess(
        (val) => (val === '' || val === null ? undefined : val),
        z.coerce.date().optional()
      )
      .optional(),
    endDate: z
      .preprocess(
        (val) => (val === '' || val === null ? undefined : val),
        z.coerce.date().optional()
      )
      .optional(),
    machine: z.string().optional(),
  })
  .merge(BasePaginateParamsSchema)
  .transform((values) => ({
    ...values,
    mfgYear: values.mfgDate ? dayjs(values.mfgDate).isoWeekYear() : undefined,
    mfgWeek: values.mfgDate ? dayjs(values.mfgDate).isoWeek() : undefined,
    status: values.filterStatus || values.status,
  }))

export type Prd4010SearchParams = z.infer<typeof Prd4010SearchParamsSchema>

export const Prd4010SearchResultModelSchema = z.object({
  id: z.number(),
  deliveryDate: z.coerce.date(),
  deliveryWeekNo: z.number().int(),
  productionDate: z.coerce.date(),
  productionWeekNo: z.number().int(),
  partyId: z.number(),
  partyName: z.string(),
  itemId: z.number(),
  itemCode: z.string(),
  itemName: z.string(),
  qty: z.number(),
  number: z.string(),
  lotNo: z.string().nullable(),
  productionType: z.string().nullable(),
  machine: z.string().nullable(),
  startDate: z.coerce.date().nullable(),
  endDate: z.coerce.date().nullable(),
  status: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  isTestProduction: z.boolean(),
})

export type Prd4010SearchResultModel = z.infer<
  typeof Prd4010SearchResultModelSchema
>

export const Prd4010SearchResultSchema = z
  .object({
    data: z.array(Prd4010SearchResultModelSchema),
  })
  .merge(BasePaginateResultSchema)

export type Prd4010SearchResult = z.infer<typeof Prd4010SearchResultSchema>
